// src/pages/DriverSignature.js - UPDATED: CAMERA ROLL UPLOAD SUPPORT
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../services/firebase';
import SignatureCapture from '../components/SignatureCapture';
import { generateBOL } from '../services/bolService';

// Function to generate display ID (same as customer dashboard)
const generateDisplayId = (firebaseId, index) => {
  if (firebaseId && firebaseId.includes('_')) {
    const parts = firebaseId.split('_');
    if (parts.length >= 3) {
      const timestamp = parts[1];
      const randomPart = parts[2];
      return `SH${timestamp.slice(-6)}${randomPart.substring(0, 4).toUpperCase()}`;
    }
  }
  return `SH${String(index + 1).padStart(6, '0')}`;
};

// Simple Photo Preview Component
const PhotoPreview = ({ photos, onRemove, location, uploadProgress, isUploading }) => (
  <div className="mt-4">
    <p className="text-sm font-medium text-gray-700 mb-2">
      Uploaded Photos ({photos.length})
    </p>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {photos.map((photo, index) => (
        <div key={index} className="relative border rounded-lg p-2 bg-white shadow-sm">
          <img 
            src={photo.preview} 
            alt={`${location} photo ${index + 1}`}
            className="w-full h-24 object-cover rounded"
          />
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
            disabled={isUploading}
          >
            ×
          </button>
          <p className="text-xs mt-1 truncate text-gray-600">{photo.file.name}</p>
          <p className="text-xs text-gray-400">
            {(photo.file.size / 1024 / 1024).toFixed(1)} MB
          </p>
          {uploadProgress[photo.file.name] && (
            <p className={`text-xs mt-1 ${
              uploadProgress[photo.file.name].includes('✅') ? 'text-green-600' : 
              uploadProgress[photo.file.name].includes('❌') ? 'text-red-600' : 'text-blue-600'
            }`}>
              {uploadProgress[photo.file.name]}
            </p>
          )}
        </div>
      ))}
    </div>
  </div>
);

// Photo Upload Options Component
const PhotoUploadOptions = ({ onCameraUpload, onGalleryUpload, location, isUploading, photosAlreadyUploaded }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Camera Upload Option */}
      <div className="border-2 border-dashed border-blue-300 rounded-lg p-6 text-center hover:bg-blue-50 transition-colors cursor-pointer">
        <input
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          onChange={onCameraUpload}
          className="hidden"
          id={`camera-upload-${location}`}
          disabled={isUploading || photosAlreadyUploaded}
        />
        <label 
          htmlFor={`camera-upload-${location}`}
          className="cursor-pointer block"
        >
          <div className="w-12 h-12 mx-auto mb-3 bg-blue-100 rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <h4 className="font-semibold text-gray-900 mb-1">Take New Photos</h4>
          <p className="text-sm text-gray-600">Use your camera to capture new photos</p>
          <p className="text-xs text-gray-500 mt-2">Fast direct upload</p>
        </label>
      </div>

      {/* Gallery Upload Option */}
      <div className="border-2 border-dashed border-green-300 rounded-lg p-6 text-center hover:bg-green-50 transition-colors cursor-pointer">
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={onGalleryUpload}
          className="hidden"
          id={`gallery-upload-${location}`}
          disabled={isUploading || photosAlreadyUploaded}
        />
        <label 
          htmlFor={`gallery-upload-${location}`}
          className="cursor-pointer block"
        >
          <div className="w-12 h-12 mx-auto mb-3 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <h4 className="font-semibold text-gray-900 mb-1">Choose from Gallery</h4>
          <p className="text-sm text-gray-600">Select existing photos from your device</p>
          <p className="text-xs text-gray-500 mt-2">Upload from camera roll</p>
        </label>
      </div>
    </div>

    {/* Quick Upload Button for Both Options */}
    <div className="text-center">
      <div className="inline-flex gap-2">
        <input
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          onChange={onCameraUpload}
          className="hidden"
          id={`quick-camera-${location}`}
        />
        <label 
          htmlFor={`quick-camera-${location}`}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer inline-flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
          </svg>
          Take Photos
        </label>

        <input
          type="file"
          accept="image/*"
          multiple
          onChange={onGalleryUpload}
          className="hidden"
          id={`quick-gallery-${location}`}
        />
        <label 
          htmlFor={`quick-gallery-${location}`}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors cursor-pointer inline-flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Choose from Gallery
        </label>
      </div>
    </div>
  </div>
);

const DriverSignature = () => {
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [capturingSignature, setCapturingSignature] = useState(false);
  const [generatingBOL, setGeneratingBOL] = useState(false);
  const [pickupPhotos, setPickupPhotos] = useState([]);
  const [deliveryPhotos, setDeliveryPhotos] = useState([]);
  const [pickupComment, setPickupComment] = useState('');
  const [deliveryComment, setDeliveryComment] = useState('');
  const [photoError, setPhotoError] = useState('');
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [securityCheckPassed, setSecurityCheckPassed] = useState(false);
  const [photosUploaded, setPhotosUploaded] = useState({
    pickup: false,
    delivery: false
  });
  
  const { shipmentId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');
  const driverId = searchParams.get('driverId');
  const stepType = searchParams.get('step'); // 'pickup' or 'delivery'

  const steps = [
    { number: 1, type: 'pickup', title: 'Pickup Documentation', description: 'Take photos and capture signatures at pickup location' },
    { number: 2, type: 'delivery', title: 'Delivery Documentation', description: 'Take photos and capture signatures at delivery location' }
  ];

  // Determine current step based on URL parameter or existing data
  const currentStepConfig = stepType === 'delivery' 
    ? steps.find(step => step.type === 'delivery') 
    : steps.find(step => step.type === 'pickup');

  // ENHANCED SECURITY: Additional security checks
  useEffect(() => {
    const performSecurityChecks = () => {
      // Check if user is trying to access admin routes
      const currentPath = window.location.pathname;
      if (currentPath.includes('/admin') || currentPath.includes('/dashboard') || currentPath.includes('/profile')) {
        setError('Access denied. Driver portal cannot access admin areas.');
        return false;
      }

      // Check for admin session storage or tokens
      if (localStorage.getItem('adminToken') || sessionStorage.getItem('adminSession')) {
        setError('Security violation detected. Please use the driver portal only.');
        return false;
      }

      return true;
    };

    if (!performSecurityChecks()) {
      setLoading(false);
      return;
    }
    setSecurityCheckPassed(true);
  }, []);

  useEffect(() => {
    const fetchShipment = async () => {
      try {
        // ENHANCED SECURITY: Additional parameter validation
        if (!token || !driverId || !shipmentId || !stepType) {
          setError('Invalid signature link. Missing required parameters.');
          return;
        }

        // ENHANCED SECURITY: Validate parameter format
        if (token.length < 10 || driverId.length < 5 || shipmentId.length < 5) {
          setError('Invalid parameter format. Please use a valid driver link.');
          return;
        }

        // Validate step type
        if (!['pickup', 'delivery'].includes(stepType)) {
          setError('Invalid step type. Please use a valid driver link.');
          return;
        }

        const shipmentDoc = await getDoc(doc(db, 'shipments', shipmentId));
        if (!shipmentDoc.exists()) {
          setError('Shipment not found');
          return;
        }

        const shipmentData = shipmentDoc.data();
        
        // ENHANCED SECURITY: Comprehensive token validation
        const driverToken = stepType === 'pickup' 
          ? shipmentData.assignedDriver?.pickupToken
          : shipmentData.assignedDriver?.deliveryToken;
          
        if (driverToken !== token || shipmentData.assignedDriver?.driverId !== driverId) {
          setError('Invalid or expired signature link. Please contact your dispatcher.');
          return;
        }

        // ENHANCED SECURITY: Check if driver is still assigned and active
        if (shipmentData.assignedDriver?.status === 'revoked' || 
            shipmentData.assignedDriver?.status === 'cancelled') {
          setError('Driver assignment has been revoked. Please contact your dispatcher.');
          return;
        }

        // ENHANCED SECURITY: Check if shipment is still active
        if (shipmentData.status === 'cancelled') {
          setError('This shipment has been cancelled.');
          return;
        }

        // Check if step is already completed
        if (stepType === 'pickup' && shipmentData.status === 'in_transit') {
          // Allow access even if pickup is completed for signature updates
          console.log('Pickup already completed, but allowing access for documentation');
        }

        if (stepType === 'delivery' && shipmentData.status === 'delivered') {
          setError('Delivery documentation has already been completed.');
          return;
        }

        const displayId = generateDisplayId(shipmentDoc.id, 0);
        
        // Set current step based on URL parameter
        const stepNumber = stepType === 'pickup' ? 1 : 2;
        setCurrentStep(stepNumber);

        // Check if photos are already uploaded
        const signatures = shipmentData.signatures || {};
        setPhotosUploaded({
          pickup: !!signatures.pickup_photos && signatures.pickup_photos.length > 0,
          delivery: !!signatures.delivery_photos && signatures.delivery_photos.length > 0
        });

        setShipment({ 
          id: shipmentDoc.id, 
          ...shipmentData,
          displayId: displayId
        });
      } catch (error) {
        console.error('Error fetching shipment:', error);
        setError('Error loading shipment details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (securityCheckPassed) {
      fetchShipment();
    }
  }, [shipmentId, token, driverId, securityCheckPassed, stepType]);

  // ENHANCED SECURITY: Prevent navigation to admin routes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is hidden - could be navigating away
        setTimeout(() => {
          const currentPath = window.location.pathname;
          if (currentPath.includes('/admin') || currentPath.includes('/dashboard')) {
            window.location.href = '/'; // Force redirect to home
          }
        }, 100);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // SIMPLE photo upload - NO COMPRESSION, NO RETRY LOGIC
  const uploadPhotosToStorage = async (photos, location) => {
    const uploadedPhotos = [];
    setUploadingPhotos(true);
    setUploadProgress({});
    
    try {
      for (const [index, photoData] of photos.entries()) {
        const file = photoData.file;
        
        try {
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: 'Uploading...'
          }));

          console.log(`Uploading ${location} photo:`, file.name);

          // Create unique filename
          const timestamp = Date.now();
          const fileExtension = file.name.split('.').pop() || 'jpg';
          const fileName = `${location}_${timestamp}_${index}.${fileExtension}`;
          const photoRef = ref(storage, `shipments/${shipmentId}/photos/${fileName}`);
          
          // Upload directly without compression
          const snapshot = await uploadBytes(photoRef, file);
          const downloadURL = await getDownloadURL(snapshot.ref);
          
          uploadedPhotos.push({
            name: file.name,
            url: downloadURL,
            uploadedAt: new Date().toISOString(),
            size: file.size,
            type: file.type,
            storagePath: photoRef.fullPath
          });
          
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: '✅ Uploaded'
          }));
          
        } catch (error) {
          console.error(`Upload failed for ${file.name}:`, error);
          uploadedPhotos.push({
            name: file.name,
            url: null,
            uploadedAt: new Date().toISOString(),
            size: file.size,
            type: file.type,
            uploadError: error.message
          });
          
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: '❌ Upload failed'
          }));
        }
      }
    } finally {
      setUploadingPhotos(false);
    }
    
    return uploadedPhotos;
  };

  // ✅ UPDATED: Enhanced photo upload handler with camera roll support
  const handlePhotoUpload = (event, location, sourceType) => {
    const files = Array.from(event.target.files);
    
    if (files.length === 0) return;

    // Validate file types and sizes
    const validFiles = files.filter(file => {
      const isValidType = file.type.startsWith('image/');
      const isValidSize = file.size <= 50 * 1024 * 1024; // 50MB limit
      
      if (!isValidType) {
        alert(`❌ ${file.name} is not a valid image file. Please select only image files.`);
        return false;
      }
      
      if (!isValidSize) {
        alert(`❌ ${file.name} is too large. Please select files smaller than 50MB.`);
        return false;
      }
      
      return true;
    });

    if (validFiles.length === 0) return;

    // Create photo objects with file reference and preview
    const photosWithData = validFiles.map(file => {
      const preview = URL.createObjectURL(file);
      return { 
        file, 
        preview,
        source: sourceType // 'camera' or 'gallery'
      };
    });

    if (location === 'pickup') {
      setPickupPhotos(prev => [...prev, ...photosWithData]);
    } else {
      setDeliveryPhotos(prev => [...prev, ...photosWithData]);
    }
    setPhotoError('');
    
    // Clear the file input
    event.target.value = '';
  };

  // ✅ NEW: Separate handlers for camera and gallery uploads
  const handleCameraUpload = (event, location) => {
    handlePhotoUpload(event, location, 'camera');
  };

  const handleGalleryUpload = (event, location) => {
    handlePhotoUpload(event, location, 'gallery');
  };

  const removePhoto = (index, location) => {
    if (location === 'pickup') {
      setPickupPhotos(prev => {
        const newPhotos = prev.filter((_, i) => i !== index);
        // Revoke object URL to prevent memory leaks
        if (prev[index].preview) {
          URL.revokeObjectURL(prev[index].preview);
        }
        return newPhotos;
      });
    } else {
      setDeliveryPhotos(prev => {
        const newPhotos = prev.filter((_, i) => i !== index);
        if (prev[index].preview) {
          URL.revokeObjectURL(prev[index].preview);
        }
        return newPhotos;
      });
    }
  };

  // ✅ UPDATED: Check if photos are required ONLY ONCE at the beginning of the step
  const checkPhotosRequired = () => {
    const currentPhotos = currentStepConfig.type === 'pickup' ? pickupPhotos : deliveryPhotos;
    
    // ✅ UPDATED: Safely access photosUploaded with default values
    const alreadyUploaded = currentStepConfig.type === 'pickup' 
      ? (photosUploaded?.pickup || false) 
      : (photosUploaded?.delivery || false);
    
    // ✅ UPDATED: Only check if photos haven't been uploaded yet
    if (currentPhotos.length === 0 && !alreadyUploaded) {
      setPhotoError(`Please upload at least one photo for ${currentStepConfig.type} verification.`);
      return false;
    }
    setPhotoError('');
    return true;
  };

  // ✅ UPDATED: Upload photos for current step if needed - ONLY ONCE
  const uploadCurrentStepPhotos = async () => {
    const currentStepType = currentStepConfig.type;
    const currentPhotos = currentStepType === 'pickup' ? pickupPhotos : deliveryPhotos;
    
    // ✅ UPDATED: Safely access photosUploaded with default values
    const alreadyUploaded = currentStepType === 'pickup' 
      ? (photosUploaded?.pickup || false)
      : (photosUploaded?.delivery || false);
    
    // If photos are already uploaded, return them
    if (alreadyUploaded) {
      // ✅ UPDATED: Safely access shipment signatures with default empty object
      const currentSignatures = shipment?.signatures || {};
      
      if (currentStepType === 'pickup') {
        // ✅ UPDATED: Return empty array if pickup_photos is undefined
        return currentSignatures.pickup_photos || [];
      } else {
        // ✅ UPDATED: Return empty array if delivery_photos is undefined
        return currentSignatures.delivery_photos || [];
      }
    }

    // Upload new photos ONLY if we have photos to upload
    if (currentPhotos.length > 0) {
      const uploadedPhotos = await uploadPhotosToStorage(currentPhotos, currentStepType);
      const successfulPhotos = uploadedPhotos.filter(photo => photo.url !== null);
      
      if (successfulPhotos.length === 0) {
        throw new Error('All photo uploads failed. Please check your connection and try again.');
      }
      
      // Mark photos as uploaded for this step
      setPhotosUploaded(prev => ({
        ...prev,
        [currentStepType]: true
      }));
      
      return successfulPhotos;
    }
    
    return [];
  };

  // ✅ UPDATED: Handle pickup signatures - PHOTOS CHECKED ONLY ONCE
  const handlePickupCustomerSignatureSave = async (signatureData) => {
    await handlePickupSignatureSave(signatureData, 'customer');
  };

  const handlePickupDriverSignatureSave = async (signatureData) => {
    await handlePickupSignatureSave(signatureData, 'driver');
  };

  const handlePickupSignatureSave = async (signatureData, signatureType) => {
    try {
      // ✅ UPDATED: Check photos only once at the beginning
      if (!checkPhotosRequired()) {
        return;
      }

      setPhotoError('');
      setCapturingSignature(true);
      
      // ✅ UPDATED: Upload photos ONCE for the entire step
      let successfulPhotos = [];
      try {
        successfulPhotos = await uploadCurrentStepPhotos();
      } catch (error) {
        alert(error.message);
        return;
      }

      const firestoreSignatureType = `pickup_${signatureType}`;
      
      const updateData = {
        updatedAt: serverTimestamp(),
        [`signatures.${firestoreSignatureType}`]: {
          ...signatureData,
          signedViaDriverLink: true,
          driverAssignmentId: driverId,
          timestamp: serverTimestamp()
        }
      };

      // ✅ UPDATED: Safely check photosUploaded with default value
      const pickupPhotosUploaded = photosUploaded?.pickup || false;
      
      // ✅ UPDATED: Safely check successfulPhotos length
      const hasSuccessfulPhotos = successfulPhotos && successfulPhotos.length > 0;
      
      // Only add pickup photos if they're not already saved and we have new ones
      if (hasSuccessfulPhotos && !pickupPhotosUploaded) {
        updateData[`signatures.pickup_photos`] = successfulPhotos;
      }

      if (pickupComment) {
        updateData[`signatures.pickup_comment`] = pickupComment;
      }

      await updateDoc(doc(db, 'shipments', shipmentId), updateData);
      
      // Get updated shipment data to check current signature status
      const updatedShipmentDoc = await getDoc(doc(db, 'shipments', shipmentId));
      const updatedShipment = updatedShipmentDoc.data();
      const signatures = updatedShipment.signatures || {};
      
      const hasCustomerSignature = signatures.pickup_customer?.signature || signatures.pickup_customer?.skipped;
      const hasDriverSignature = signatures.pickup_driver?.signature;
      
      console.log('Current pickup signature status after save:', {
        hasCustomerSignature,
        hasDriverSignature,
        signatureTypeSaved: signatureType,
        customerSignature: signatures.pickup_customer?.signature ? 'exists' : 'missing',
        driverSignature: signatures.pickup_driver?.signature ? 'exists' : 'missing'
      });

      if (hasCustomerSignature && hasDriverSignature) {
        // Both pickup signatures are complete - mark as picked up
        await updateDoc(doc(db, 'shipments', shipmentId), {
          status: 'in_transit',
          pickedUpAt: serverTimestamp(),
          assignedDriver: {
            ...shipment.assignedDriver,
            pickupCompletedAt: serverTimestamp(),
            status: 'in_transit',
            pickupToken: null // Invalidate pickup token after completion
          },
          updatedAt: serverTimestamp()
        });

        alert('✅ Pickup completed successfully! Both signatures captured. Please wait for delivery link from dispatcher.');
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        // Only one signature is captured - show appropriate message
        if (signatureType === 'driver') {
          alert('✅ Driver pickup signature saved! Please capture customer pickup signature to complete pickup.');
        } else {
          alert('✅ Customer pickup signature saved! Please provide your driver pickup signature to complete pickup.');
        }
      }
    } catch (error) {
      console.error('Error saving pickup signature:', error);
      alert('❌ Error saving pickup signature. Please try again.');
    } finally {
      setCapturingSignature(false);
    }
  };

  // ✅ UPDATED: Skip customer signature at pickup - PHOTOS CHECKED ONLY ONCE
  const handleSkipPickupCustomerSignature = async () => {
    try {
      // ✅ UPDATED: Check photos only once at the beginning
      if (!checkPhotosRequired()) {
        return;
      }

      setPhotoError('');
      setCapturingSignature(true);
      
      // ✅ UPDATED: Upload photos ONCE for the entire step
      let successfulPhotos = [];
      try {
        successfulPhotos = await uploadCurrentStepPhotos();
      } catch (error) {
        alert(error.message);
        return;
      }

      const updateData = {
        updatedAt: serverTimestamp(),
        [`signatures.pickup_customer`]: {
          skipped: true,
          reason: 'Customer not present at pickup',
          skippedAt: serverTimestamp(),
          signedViaDriverLink: true,
          driverAssignmentId: driverId
        }
      };

      // ✅ UPDATED: Safely check photosUploaded with default value
      const pickupPhotosUploaded = photosUploaded?.pickup || false;

      // ✅ UPDATED: Safely check successfulPhotos length
      const hasSuccessfulPhotos = successfulPhotos && successfulPhotos.length > 0;

      // Only add pickup photos if they're not already saved and we have new ones
      if (hasSuccessfulPhotos && !pickupPhotosUploaded) {
        updateData[`signatures.pickup_photos`] = successfulPhotos;
      }

      if (pickupComment && !shipment?.signatures?.pickup_comment) {
        updateData[`signatures.pickup_comment`] = pickupComment;
      }

      await updateDoc(doc(db, 'shipments', shipmentId), updateData);
      
      // Get updated shipment data to check current signature status
      const updatedShipmentDoc = await getDoc(doc(db, 'shipments', shipmentId));
      const updatedShipment = updatedShipmentDoc.data();
      const signatures = updatedShipment.signatures || {};
      const hasDriverSignature = signatures.pickup_driver?.signature;

      console.log('After skipping pickup customer signature:', {
        customerSkipped: signatures.pickup_customer?.skipped,
        hasDriverSignature,
        pickupPhotos: signatures.pickup_photos?.length
      });

      if (hasDriverSignature) {
        // Both are complete (customer skipped + driver signed) - complete pickup
        await updateDoc(doc(db, 'shipments', shipmentId), {
          status: 'in_transit',
          pickedUpAt: serverTimestamp(),
          assignedDriver: {
            ...shipment.assignedDriver,
            pickupCompletedAt: serverTimestamp(),
            status: 'in_transit',
            pickupToken: null // Invalidate pickup token after completion
          },
          updatedAt: serverTimestamp()
        });

        alert('✅ Pickup completed! Customer was not present at pickup. Please wait for delivery link from dispatcher.');
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        alert('✅ Customer pickup signature skipped. Please provide your driver pickup signature to complete pickup.');
      }
    } catch (error) {
      console.error('Error skipping pickup signature:', error);
      alert('❌ Error skipping pickup signature. Please try again.');
    } finally {
      setCapturingSignature(false);
    }
  };

  // ✅ UPDATED: Handle delivery signatures - PHOTOS CHECKED ONLY ONCE
  const handleDeliveryCustomerSignatureSave = async (signatureData) => {
    await handleDeliverySignatureSave(signatureData, 'customer');
  };

  const handleDeliveryDriverSignatureSave = async (signatureData) => {
    await handleDeliverySignatureSave(signatureData, 'driver');
  };

  const handleDeliverySignatureSave = async (signatureData, signatureType) => {
    try {
      // ✅ UPDATED: Check photos only once at the beginning
      if (!checkPhotosRequired()) {
        return;
      }

      setPhotoError('');
      setCapturingSignature(true);
      
      // ✅ UPDATED: Upload photos ONCE for the entire step
      let successfulPhotos = [];
      try {
        successfulPhotos = await uploadCurrentStepPhotos();
      } catch (error) {
        alert(error.message);
        return;
      }

      const firestoreSignatureType = `delivery_${signatureType}`;
      
      const updateData = {
        updatedAt: serverTimestamp(),
        [`signatures.${firestoreSignatureType}`]: {
          ...signatureData,
          signedViaDriverLink: true,
          driverAssignmentId: driverId,
          timestamp: serverTimestamp()
        }
      };

      // ✅ UPDATED: Safely check photosUploaded with default value
      const deliveryPhotosUploaded = photosUploaded?.delivery || false;
      
      // ✅ UPDATED: Safely check successfulPhotos length
      const hasSuccessfulPhotos = successfulPhotos && successfulPhotos.length > 0;
      
      // Only add delivery photos if they're not already saved and we have new ones
      if (hasSuccessfulPhotos && !deliveryPhotosUploaded) {
        updateData[`signatures.delivery_photos`] = successfulPhotos;
      }

      if (deliveryComment && !shipment?.signatures?.delivery_comment) {
        updateData[`signatures.delivery_comment`] = deliveryComment;
      }

      await updateDoc(doc(db, 'shipments', shipmentId), updateData);
      
      // Get updated shipment data to check current signature status
      const updatedShipmentDoc = await getDoc(doc(db, 'shipments', shipmentId));
      const updatedShipment = updatedShipmentDoc.data();
      const signatures = updatedShipment.signatures || {};
      
      const hasCustomerSignature = signatures.delivery_customer?.signature || signatures.delivery_customer?.skipped;
      const hasDriverSignature = signatures.delivery_driver?.signature;
      
      console.log('Current delivery signature status after save:', {
        hasCustomerSignature,
        hasDriverSignature,
        signatureTypeSaved: signatureType,
        customerSignature: signatures.delivery_customer?.signature ? 'exists' : 'missing',
        driverSignature: signatures.delivery_driver?.signature ? 'exists' : 'missing'
      });

      if (hasCustomerSignature && hasDriverSignature) {
        // Both signatures are complete - generate BOL and complete delivery
        const bolGenerated = await checkAndGenerateBOL();
        
        await updateDoc(doc(db, 'shipments', shipmentId), {
          status: 'delivered',
          deliveredAt: serverTimestamp(),
          customerCanViewBOL: true,
          assignedDriver: {
            ...shipment.assignedDriver,
            completedAt: serverTimestamp(),
            status: 'completed',
            deliveryToken: null // Invalidate delivery token after completion
          },
          updatedAt: serverTimestamp()
        });

        if (bolGenerated) {
          alert('✅ Delivery completed successfully! BOL has been automatically generated with photos and comments.');
        } else {
          alert('✅ Delivery completed successfully! All signatures captured.');
        }
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        // Only one signature is captured - show appropriate message
        if (signatureType === 'driver') {
          alert('✅ Driver delivery signature saved! Please capture customer delivery signature to complete delivery.');
        } else {
          alert('✅ Customer delivery signature saved! Please provide your driver delivery signature to complete delivery.');
        }
      }
    } catch (error) {
      console.error('Error saving delivery signature:', error);
      alert('❌ Error saving delivery signature. Please try again.');
    } finally {
      setCapturingSignature(false);
    }
  };

  // ✅ UPDATED: Skip customer signature at delivery - PHOTOS CHECKED ONLY ONCE
  const handleSkipDeliveryCustomerSignature = async () => {
    try {
      // ✅ UPDATED: Check photos only once at the beginning
      if (!checkPhotosRequired()) {
        return;
      }

      setPhotoError('');
      setCapturingSignature(true);
      
      // ✅ UPDATED: Upload photos ONCE for the entire step
      let successfulPhotos = [];
      try {
        successfulPhotos = await uploadCurrentStepPhotos();
      } catch (error) {
        alert(error.message);
        return;
      }

      const updateData = {
        updatedAt: serverTimestamp(),
        [`signatures.delivery_customer`]: {
          skipped: true,
          reason: 'Customer not present at delivery',
          skippedAt: serverTimestamp(),
          signedViaDriverLink: true,
          driverAssignmentId: driverId
        }
      };

      // ✅ UPDATED: Safely check photosUploaded with default value
      const deliveryPhotosUploaded = photosUploaded?.delivery || false;

      // ✅ UPDATED: Safely check successfulPhotos length
      const hasSuccessfulPhotos = successfulPhotos && successfulPhotos.length > 0;

      // Only add delivery photos if they're not already saved and we have new ones
      if (hasSuccessfulPhotos && !deliveryPhotosUploaded) {
        updateData[`signatures.delivery_photos`] = successfulPhotos;
      }

      if (deliveryComment && !shipment?.signatures?.delivery_comment) {
        updateData[`signatures.delivery_comment`] = deliveryComment;
      }

      await updateDoc(doc(db, 'shipments', shipmentId), updateData);
      
      // Get updated shipment data to check current signature status
      const updatedShipmentDoc = await getDoc(doc(db, 'shipments', shipmentId));
      const updatedShipment = updatedShipmentDoc.data();
      const signatures = updatedShipment.signatures || {};
      const hasDriverSignature = signatures.delivery_driver?.signature;

      console.log('After skipping delivery customer signature:', {
        customerSkipped: signatures.delivery_customer?.skipped,
        hasDriverSignature,
        deliveryPhotos: signatures.delivery_photos?.length
      });

      if (hasDriverSignature) {
        // Both are complete (customer skipped + driver signed) - generate BOL and complete
        const bolGenerated = await checkAndGenerateBOL();
        
        await updateDoc(doc(db, 'shipments', shipmentId), {
          status: 'delivered',
          deliveredAt: serverTimestamp(),
          customerCanViewBOL: true,
          assignedDriver: {
            ...shipment.assignedDriver,
            completedAt: serverTimestamp(),
            status: 'completed',
            deliveryToken: null // Invalidate delivery token after completion
          },
          updatedAt: serverTimestamp()
        });

        if (bolGenerated) {
          alert('✅ Delivery completed! Customer was not present at delivery. BOL has been automatically generated with photos and comments.');
        } else {
          alert('✅ Delivery completed! Customer was not present at delivery.');
        }
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        alert('✅ Customer delivery signature skipped. Please provide your driver delivery signature to complete delivery.');
      }
    } catch (error) {
      console.error('Error skipping delivery signature:', error);
      alert('❌ Error skipping delivery signature. Please try again.');
    } finally {
      setCapturingSignature(false);
    }
  };

  // ✅ UPDATED: BOL generation with proper displayId
  const checkAndGenerateBOL = async () => {
    try {
      setGeneratingBOL(true);
      
      const shipmentDoc = await getDoc(doc(db, 'shipments', shipmentId));
      if (!shipmentDoc.exists()) {
        console.error('Shipment not found when generating BOL');
        return false;
      }

      const shipmentData = shipmentDoc.data();
      const signatures = shipmentData.signatures || {};

      // ✅ Use the displayId from the current shipment state or generate it
      const displayId = shipment?.displayId || generateDisplayId(shipmentDoc.id, 0);
      
      // ✅ Create complete shipment object with displayId
      const completeShipmentData = { 
        id: shipmentDoc.id, 
        ...shipmentData,
        displayId: displayId // ✅ Ensure displayId is included
      };

      const hasPickupCustomerSignature = signatures.pickup_customer?.signature || signatures.pickup_customer?.skipped;
      const hasPickupDriverSignature = signatures.pickup_driver?.signature;
      const hasDeliveryCustomerSignature = signatures.delivery_customer?.signature || signatures.delivery_customer?.skipped;
      const hasDeliveryDriverSignature = signatures.delivery_driver?.signature;

      console.log('Checking signatures for BOL generation:', {
        hasPickupCustomerSignature,
        hasPickupDriverSignature,
        hasDeliveryCustomerSignature,
        hasDeliveryDriverSignature,
        pickupPhotos: signatures.pickup_photos,
        deliveryPhotos: signatures.delivery_photos,
        pickupComment: signatures.pickup_comment,
        deliveryComment: signatures.delivery_comment,
        displayId: displayId
      });

      if (hasPickupCustomerSignature && hasPickupDriverSignature && 
          hasDeliveryCustomerSignature && hasDeliveryDriverSignature) {
        console.log('All signatures complete - generating BOL automatically');
        console.log('Shipment data passed to BOL generator:', {
          id: completeShipmentData.id,
          displayId: completeShipmentData.displayId,
          invoiceNumber: completeShipmentData.invoiceNumber
        });
        
        try {
          // ✅ PASS the complete shipment data with displayId to BOL generator
          const bolUrl = await generateBOL(completeShipmentData, {}, { uid: driverId });
          
          await updateDoc(doc(db, 'shipments', shipmentId), {
            bolUrl: bolUrl,
            bolGeneratedAt: serverTimestamp(),
            customerCanViewBOL: true,
            updatedAt: serverTimestamp()
          });

          console.log('BOL generated successfully with photos and comments:', bolUrl);
          return true;
        } catch (bolError) {
          console.error('Error in BOL generation:', bolError);
          return false;
        }
      }
      
      return false;
    } catch (error) {
      console.error('Error checking/generating BOL:', error);
      return false;
    } finally {
      setGeneratingBOL(false);
    }
  };

  const getStepStatus = (stepNumber) => {
    if (stepNumber < currentStep) return 'completed';
    if (stepNumber === currentStep) return 'current';
    return 'upcoming';
  };

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      [...pickupPhotos, ...deliveryPhotos].forEach(photo => {
        if (photo.preview) {
          URL.revokeObjectURL(photo.preview);
        }
      });
    };
  }, [pickupPhotos, deliveryPhotos]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading shipment details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md mx-auto text-center bg-white p-8 rounded-lg shadow-md">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-y-3">
            <p className="text-sm text-gray-500">If you believe this is an error, please contact:</p>
            <div className="text-sm">
              <p><strong>Flex Logistics Management</strong></p>
              <p>Email: contact@flexlogisticsmanagement.com</p>
              <p>Phone: (509) 601-9079</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/')}
            className="mt-6 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  const currentPhotos = currentStepConfig.type === 'pickup' ? pickupPhotos : deliveryPhotos;
  const photosAlreadyUploaded = currentStepConfig.type === 'pickup' ? (photosUploaded?.pickup || false) : (photosUploaded?.delivery || false);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {currentStepConfig.type === 'pickup' ? 'Pickup Documentation' : 'Delivery Documentation'}
          </h1>
          <p className="text-gray-600">
            {currentStepConfig.type === 'pickup' 
              ? 'Capture photos and BOTH signatures at pickup location'
              : 'Capture photos and BOTH signatures at delivery location'}
          </p>
          {/* ENHANCED SECURITY: Show security status */}
          <div className="mt-2 p-2 bg-green-100 border border-green-300 rounded inline-block">
            <p className="text-green-700 text-sm">🔒 Secure Driver Portal - {currentStepConfig.type.toUpperCase()} Step</p>
          </div>
          {uploadingPhotos && (
            <div className="mt-2 p-2 bg-blue-100 border border-blue-300 rounded">
              <p className="text-blue-700 text-sm">
                📸 {Object.values(uploadProgress).filter(status => status.includes('✅')).length}/{currentPhotos.length} photos uploaded
              </p>
            </div>
          )}
          {generatingBOL && (
            <div className="mt-2 p-2 bg-blue-100 border border-blue-300 rounded">
              <p className="text-blue-700 text-sm">🔄 Generating Bill of Lading with embedded photos...</p>
            </div>
          )}
          {photosAlreadyUploaded && (
            <div className="mt-2 p-2 bg-green-100 border border-green-300 rounded">
              <p className="text-green-700 text-sm">✅ Photos already uploaded for this step</p>
            </div>
          )}
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            {steps.map((step, index) => (
              <React.Fragment key={step.number}>
                <div className={`flex flex-col items-center ${
                  step.type === currentStepConfig.type ? 'text-blue-600' : 
                  step.number < currentStep ? 'text-green-600' : 'text-gray-400'
                }`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                    step.type === currentStepConfig.type ? 'bg-blue-100' : 
                    step.number < currentStep ? 'bg-green-100' : 'bg-gray-100'
                  }`}>
                    {step.number < currentStep ? '✓' : step.number}
                  </div>
                  <span className="mt-2 text-xs font-medium text-center max-w-20">
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-1 mx-2 ${
                    step.number < currentStep ? 'bg-green-600' : 'bg-gray-200'
                  }`}></div>
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="text-center mt-4 text-sm text-gray-500">
            {currentStepConfig.type === 'pickup' ? (
              <>📸 Photos required at pickup location<br />✍️ BOTH signatures required for pickup<br />✅ Mark shipment as picked up</>
            ) : (
              <>📸 Photos required at delivery location<br />✍️ BOTH signatures required for delivery<br />📄 BOL auto-generated with embedded photos</>
            )}
          </div>
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          {/* Shipment Information Card */}
          <div className="bg-blue-50 border-b border-blue-200 p-6">
            <h2 className="text-xl font-semibold text-blue-800 mb-4">Shipment Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p><strong>Shipment ID:</strong> {shipment.displayId}</p>
                <p><strong>Item Description:</strong> {shipment.itemDescription}</p>
                <p><strong>Customer:</strong> {shipment.userName}</p>
                {shipment.phone && <p><strong>Customer Phone:</strong> {shipment.phone}</p>}
              </div>
              <div>
                <p><strong>Assigned Driver:</strong> {shipment.assignedDriver?.driverName}</p>
                <p><strong>Vehicle:</strong> {shipment.assignedDriver?.vehicleInfo || 'Not specified'}</p>
                <p><strong>Current Step:</strong> 
                  <span className="ml-2 px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                    {currentStepConfig.type.toUpperCase()}
                  </span>
                </p>
              </div>
              <div className="md:col-span-2">
                <p><strong>Pickup Address:</strong> {shipment.pickupAddress}</p>
              </div>
              <div className="md:col-span-2">
                <p><strong>Delivery Address:</strong> {shipment.dropoffAddress}</p>
              </div>
              {shipment.specialInstructions && (
                <div className="md:col-span-2">
                  <p><strong>Special Instructions:</strong> {shipment.specialInstructions}</p>
                </div>
              )}
            </div>
          </div>

          {/* Current Step Content */}
          <div className="p-6">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {currentStepConfig.title}
                <span className="ml-2 text-sm text-red-600">📸 Photos Required</span>
              </h3>
              <p className="text-gray-600">
                {currentStepConfig.type === 'pickup' 
                  ? 'Take photos and capture BOTH signatures at the pickup location. Both signatures are required to complete pickup.'
                  : 'Take photos and capture BOTH signatures at the delivery location. BOL will be auto-generated with embedded photos.'}
              </p>
            </div>

            {/* ✅ UPDATED: Enhanced Photo Upload Section with Camera Roll Support */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Upload Photos ({currentStepConfig.type === 'pickup' ? 'Pickup' : 'Delivery'} Location) 
                <span className="text-red-600 ml-1">*</span>
                {photosAlreadyUploaded && (
                  <span className="ml-2 text-green-600 text-sm">✅ Already uploaded</span>
                )}
              </label>
              
              <PhotoUploadOptions
                onCameraUpload={(e) => handleCameraUpload(e, currentStepConfig.type)}
                onGalleryUpload={(e) => handleGalleryUpload(e, currentStepConfig.type)}
                location={currentStepConfig.type}
                isUploading={uploadingPhotos}
                photosAlreadyUploaded={photosAlreadyUploaded}
              />
              
              <p className="text-xs text-gray-500 mt-4 text-center">
                {photosAlreadyUploaded 
                  ? 'Photos already uploaded for this step. You can proceed with signatures.'
                  : `Choose how you want to upload ${currentStepConfig.type} photos`
                }
              </p>
              
              {photoError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                  {photoError}
                </div>
              )}
              
              {/* Enhanced Photo Preview */}
              {currentPhotos.length > 0 && (
                <PhotoPreview 
                  photos={currentPhotos}
                  onRemove={(index) => removePhoto(index, currentStepConfig.type)}
                  location={currentStepConfig.type}
                  uploadProgress={uploadProgress}
                  isUploading={uploadingPhotos}
                />
              )}
            </div>

            {/* Comment Section */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Comments ({currentStepConfig.type === 'pickup' ? 'Pickup' : 'Delivery'})
              </label>
              <textarea
                value={currentStepConfig.type === 'pickup' ? pickupComment : deliveryComment}
                onChange={(e) => currentStepConfig.type === 'pickup' ? setPickupComment(e.target.value) : setDeliveryComment(e.target.value)}
                placeholder={`Enter any notes or observations about the ${currentStepConfig.type} (e.g., condition of goods, special handling, etc.)...`}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                These comments will be included in the Bill of Lading documentation
              </p>
            </div>

            {/* ✅ UPDATED: Signature Section for Pickup - NO REPEATED PHOTO CHECKS */}
            {currentStepConfig.type === 'pickup' && (
              <>
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-semibold text-blue-800 mb-2">Customer Signature Notice (Pickup)</h4>
                  <p className="text-sm text-blue-700">
                    "I hereby certify that the goods described above have been received by the carrier in apparent good order and condition, except as noted. I authorize the carrier to transport these goods to the destination specified."
                  </p>
                </div>

                <SignatureCapture
                  shipmentId={shipmentId}
                  type="customer"
                  onSave={handlePickupCustomerSignatureSave}
                  isDriver={true}
                  driverId={driverId}
                  location="pickup"
                  requirePhotos={false} // ✅ UPDATED: No photo check in component - handled once above
                  currentPhotos={pickupPhotos}
                />

                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-semibold text-green-800 mb-2">Driver Signature Notice (Pickup)</h4>
                  <p className="text-sm text-green-700">
                    "Carrier acknowledges receipt of the above described goods. Carrier certifies that the goods have been inspected and are received in the condition described, except as noted. Carrier assumes responsibility for safe transport to destination."
                  </p>
                </div>

                <SignatureCapture
                  shipmentId={shipmentId}
                  type="driver"
                  onSave={handlePickupDriverSignatureSave}
                  isDriver={true}
                  driverId={driverId}
                  location="pickup"
                  requirePhotos={false} // ✅ UPDATED: No photo check in component - handled once above
                  currentPhotos={pickupPhotos}
                />

                {/* Skip Option for Customer Signature at Pickup */}
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-yellow-800">Customer Not Present at Pickup?</h4>
                      <p className="text-sm text-yellow-700">
                        You can skip the customer signature if they are not available at pickup.
                        You will still need to provide your driver signature to complete the pickup.
                      </p>
                    </div>
                    <button
                      onClick={handleSkipPickupCustomerSignature}
                      disabled={capturingSignature || uploadingPhotos}
                      className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Skip Customer Signature
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* ✅ UPDATED: Signature Section for Delivery - NO REPEATED PHOTO CHECKS */}
            {currentStepConfig.type === 'delivery' && (
              <>
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-semibold text-blue-800 mb-2">Customer Signature Notice (Delivery)</h4>
                  <p className="text-sm text-blue-700">
                    "This is to certify that the above named materials are properly classified, packaged, marked and labeled, and are in proper condition for transportation according to the applicable regulations of the DOT. I acknowledge receipt of the goods in the condition described."
                  </p>
                </div>

                <SignatureCapture
                  shipmentId={shipmentId}
                  type="customer"
                  onSave={handleDeliveryCustomerSignatureSave}
                  isDriver={true}
                  driverId={driverId}
                  location="delivery"
                  requirePhotos={false} // ✅ UPDATED: No photo check in component - handled once above
                  currentPhotos={deliveryPhotos}
                />

                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-semibold text-green-800 mb-2">Driver Signature Notice (Delivery)</h4>
                  <p className="text-sm text-green-700">
                    "Carrier acknowledges receipt of packages and required placards. Carrier certifies emergency response information was made available and/or carrier has the DOT emergency response guidebook or equivalent documentation in the vehicle. Property described above is received in good order, except as noted."
                  </p>
                </div>

                <SignatureCapture
                  shipmentId={shipmentId}
                  type="driver"
                  onSave={handleDeliveryDriverSignatureSave}
                  isDriver={true}
                  driverId={driverId}
                  location="delivery"
                  requirePhotos={false} // ✅ UPDATED: No photo check in component - handled once above
                  currentPhotos={deliveryPhotos}
                />

                {/* Skip Option for Customer Signature at Delivery */}
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-yellow-800">Customer Not Present at Delivery?</h4>
                      <p className="text-sm text-yellow-700">
                        You can skip the customer signature if they are not available to receive the items.
                        You will still need to provide your driver signature to complete the delivery.
                      </p>
                    </div>
                    <button
                      onClick={handleSkipDeliveryCustomerSignature}
                      disabled={capturingSignature || uploadingPhotos}
                      className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Skip Customer Signature
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Instructions Box */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-2">Current Step Instructions:</h4>
              <ul className="text-sm text-blue-700 list-disc list-inside space-y-1">
                {currentStepConfig.type === 'pickup' ? (
                  <>
                    <li><strong>Take required photos</strong> - Use camera or choose from gallery</li>
                    <li><strong>Add pickup comments</strong> about the goods condition</li>
                    <li><strong>Capture BOTH signatures:</strong> Customer first, then Driver</li>
                    <li>If customer is not present, click "Skip Customer Signature"</li>
                    <li><strong>Driver signature is always required</strong></li>
                    <li>Complete pickup to proceed to delivery</li>
                  </>
                ) : (
                  <>
                    <li><strong>Take required photos</strong> - Use camera or choose from gallery</li>
                    <li><strong>Add delivery comments</strong></li>
                    <li><strong>Capture BOTH signatures:</strong> Customer first, then Driver</li>
                    <li>If customer is not present, click "Skip Customer Signature"</li>
                    <li><strong>Driver signature is always required</strong></li>
                    <li><strong>BOL auto-generated</strong> with embedded photos and all signatures</li>
                  </>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Support Information */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Need help? Contact dispatch at (509) 601-9079 or email contact@flexlogisticsmanagement.com</p>
        </div>
      </div>
    </div>
  );
};

export default DriverSignature;